import { useQuery } from '@apollo/client';
import { Box, Grid, Paper } from '@mui/material';
import { eachMonthOfInterval } from 'date-fns';
import * as Schema from 'generated/graphql/schema';
import { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSound from 'use-sound';

import AutosuggestCardHeader from '@/components/chip-filter/autosuggest-card-header';
import { ChosenOptions, Suggestions } from '@/components/chip-filter/autosuggest-chip-input';
import ResponsiveCardContent from '@/components/containers/responsive-card-content';
import ResponsiveGridContainer from '@/components/containers/responsive-grid-container';
import TimePicker from '@/components/pickers/time-picker';
import ProgressBar from '@/components/progress-bar';
import CompanyContext from '@/contexts/company';
import { useTimePicker } from '@/contexts/timepicker';
import { andonListCalls } from '@/graphql/queries';
import notification from '@/sounds/notification.mp3';
import * as Types from '@/types';
import AndonTable from '@/views/andon/table';

// FIXME: Generalize this type
interface Option {
  name: string;
  value: string;
}

const getFirstOfMonths = (from: Date, to: Date) => {
  const firstOfMonthsLocal = eachMonthOfInterval({ start: from, end: to });

  return firstOfMonthsLocal.map(
    (firstOfMonth) => new Date(firstOfMonth.getTime() - firstOfMonth.getTimezoneOffset() * (1).minutes),
  );
};

const dependentActions = {
  resolve: 'pending',
  pending: 'take',
  take: 'make',
} as const;

type FilterSchema = {
  lineId: string;
  awaitingAction: keyof typeof dependentActions;
  caller: string;
  taker: string;
  resolver: string;
  machine: string;
  unit: string;
  requestedRole: string;
};
type Filter = ChosenOptions<FilterSchema>;
type FilterSuggestions = Suggestions<FilterSchema>;

const CallsOverview: FunctionComponent = () => {
  const { from, to } = useTimePicker();

  const [t] = useTranslation(['shared', 'andon']);
  const allFirstOfMonths = useMemo(() => getFirstOfMonths(from, to || new Date()), [from.getTime(), to?.getTime()]);

  const firstOfMonthsExceptCurrent = allFirstOfMonths.filter((firstOfMonth) => {
    const now = new Date();
    return now.getUTCFullYear() !== firstOfMonth.getUTCFullYear() || now.getUTCMonth() !== firstOfMonth.getUTCMonth();
  });

  const { data, networkStatus, fetchMore } = useQuery<Schema.AndonListCallsQuery, Schema.AndonListCallsQueryVariables>(
    andonListCalls,
    {
      fetchPolicy: 'cache-and-network',
      ssr: false,
      pollInterval: (20).seconds,
      notifyOnNetworkStatusChange: true,
    },
  );

  useEffect(() => {
    firstOfMonthsExceptCurrent.forEach(async (firstOfMonth) =>
      fetchMore({
        variables: { time: firstOfMonth },
      }),
    );
  }, [from, to]);

  const [primaryAndon] = data?.andon || [];

  const { calls = [], companyId } = primaryAndon || {};

  const newestCallTime = useMemo(
    () =>
      calls.reduce((latestTimestamp, call) => {
        return Math.max(latestTimestamp, new Date(call.action.make.time).getTime());
      }, -Infinity),
    [calls],
  );

  const [play] = useSound(notification);

  useEffect(() => {
    if (!Number.isFinite(newestCallTime)) {
      return;
    }

    play();
  }, [newestCallTime]);

  const suggestions = useMemo(
    (): FilterSuggestions => [
      {
        name: t(['shared:location'], { defaultValue: 'Location' }),
        key: 'lineId',
        values: calls
          .map((c) => c.line)
          .filter((line): line is NonNullable<typeof line> => !!line)
          .filter((line, i, lines) => lines.findIndex((l) => line.id === l.id) === i)
          .map((line) => ({ name: line.name, value: line.id })),
      },
      {
        name: t(['shared:awaitingAction'], { defaultValue: 'Awaiting action' }),
        key: 'awaitingAction',
        values: [
          { name: t(['andon:take'], { defaultValue: 'Take' }), value: 'take' },
          { name: t(['andon:pending'], { defaultValue: 'Pending' }), value: 'pending' },
          { name: t(['andon:resolve'], { defaultValue: 'Resolve' }), value: 'resolve' },
        ],
      },
      {
        name: t(['shared:caller'], { defaultValue: 'Caller' }),
        key: 'caller',
        values: calls
          .map((c) => c.action.make.author)
          .filter(
            (author, i, authors) =>
              authors.findIndex((a) => author.trim().toLowerCase() === a.trim().toLowerCase()) === i,
          )
          .map((author) => ({ name: author, value: author.trim().toLowerCase() })),
      },
      {
        name: t(['shared:taker'], { defaultValue: 'Taker' }),
        key: 'taker',
        values: calls
          .map((c) => c.action.take?.author)
          .filter((author): author is NonNullable<typeof author> => !!author)
          .filter(
            (author, i, authors) =>
              authors.findIndex((a) => author.trim().toLowerCase() === a.trim().toLowerCase()) === i,
          )
          .map((author) => ({ name: author, value: author.trim().toLowerCase() })),
      },
      {
        name: t(['shared:resolver'], { defaultValue: 'Resolver' }),
        key: 'resolver',
        values: calls
          .map((c) => c.action.resolve?.author)
          .filter((author): author is NonNullable<typeof author> => !!author)
          .filter(
            (author, i, authors) =>
              authors.findIndex((a) => author.trim().toLowerCase() === a.trim().toLowerCase()) === i,
          )
          .map((author) => ({ name: author, value: author.trim().toLowerCase() })),
      },
      {
        name: t(['andon:requestedRole'], { defaultValue: 'Requested Role' }),
        key: 'requestedRole',
        values: calls
          .map((c) => c.requestedSupport)
          .filter((role): role is NonNullable<typeof role> => !!role)
          .filter(
            (role, i, roles) => roles.findIndex((r) => role.trim().toLowerCase() === r.trim().toLowerCase()) === i,
          )
          .map((role) => ({ name: role, value: role.trim().toLowerCase() })),
      },
    ],
    [t, calls],
  );

  const timeFilter = useCallback(
    (call: Schema.AndonListCallsQuery['andon'][0]['calls'][0]) => {
      const actionTime = new Date(
        (call.action.resolve || call.action.pending || call.action.take || call.action.make).time,
      ).getTime();
      return from.getTime() <= actionTime && (!to || actionTime <= to.getTime());
    },
    [from.getTime(), to?.getTime()],
  );

  const [filters, setFilters] = useState<Filter>({});

  const onFilters = useCallback((filters: Filter) => {
    const filteredFilters = Object.fromEntries(
      Object.entries(filters).filter(([filterKey], _, allFilters) => {
        if (!['machine', 'unit'].some((key) => filterKey === key)) {
          return true;
        }

        return allFilters.some(([filterKey]) => filterKey === 'lineId');
      }),
    );

    setFilters(filteredFilters);
  }, []);

  // NOTE: This is Lundbeck-specific - they are the only one that uses tags for
  // work orders - we may want to redesign that concept at a later point - leave
  // this as is for now, just know that in 99% of the cases the assignment
  // terminates immediately.
  const extendedSuggestions = useMemo((): FilterSuggestions => {
    if (!filters.lineId) {
      return suggestions;
    }

    if (!calls.some((call) => call.tags?.length)) {
      return suggestions;
    }

    const extraSuggestions: FilterSuggestions = [];

    const [machines, units] = calls
      .map((c) => c.tags)
      .flat()
      .filter((tag): tag is NonNullable<typeof tag> => !!tag)
      .filter((tag, i, tags) => tags.findIndex((t) => t.id === tag.id) === i)
      .reduce(
        ([machines, units], tag) => {
          const trimmedValue = tag.value.trim();
          const [machine, unit = ''] = trimmedValue.split(': ').map((pt) => pt.trim());

          machines[machine] = machines[machine] || { name: machine, value: machine };

          // NOTE: We use the tag.value to avoid looking up units scoped under other lines
          const lowerMachine = machine.toLowerCase();
          units[lowerMachine] = units[lowerMachine] || {};
          units[lowerMachine][trimmedValue] = units[lowerMachine][trimmedValue] || { name: unit, value: trimmedValue };

          return [machines, units];
        },
        [{}, {}] as [Record<string, Option>, Record<string, Record<string, Option>>],
      );

    if (!filters.machine) {
      extraSuggestions.push({
        name: t(['shared:machine'], { defaultValue: 'Machine' }),
        key: 'machine',
        values: Object.values(machines),
      });
    }

    const filteredMachine = filters.machine?.[0];
    if (!filteredMachine) {
      return [...suggestions, ...extraSuggestions];
    }

    const trimmedMachine = filteredMachine.trim().toLowerCase();

    extraSuggestions.push({
      name: t(['shared:unit'], { defaultValue: 'Unit' }),
      key: 'unit',
      values: Object.values(units[trimmedMachine] || {}),
    });

    return [...suggestions, ...extraSuggestions];
  }, [filters, calls]);

  // The old version of this code combined a list of filters into a list of filter functions. I combine a single filter object into one filter function. But since this was supposed to be a small refactor I just reused the part of logic that worked. So this needs to be fixed at some point
  const combinedFilters = useMemo(() => {
    return (call: Schema.AndonListCallsQuery['andon'][0]['calls'][0]): boolean => {
      // First filter by time
      if (!timeFilter(call)) {
        return false;
      }
      const allFilters = Object.entries(filters);
      if (!allFilters.length) {
        return true;
      }

      return allFilters.every(([key, values]) => {
        switch (key) {
          case 'lineId':
            return values.some((value) => value === call.line?.id);
          case 'awaitingAction':
            return values.some((value) => {
              const dependentAction = Types.has(dependentActions, value) ? dependentActions[value] : null;
              if (!dependentAction) {
                return false;
              }

              return !!call.action[dependentAction] && Types.has(call.action, value) && !call.action[value];
            });
          case 'caller':
            return values.some((value) => value.trim().toLowerCase() === call.action.make.author.trim().toLowerCase());
          case 'taker':
            return values.some((value) => {
              if (!call.action.take) {
                return false;
              }

              return value.trim().toLowerCase() === call.action.take.author.trim().toLowerCase();
            });

          case 'resolver':
            return values.some((value) => {
              if (!call.action.resolve) {
                return false;
              }

              return value.trim().toLowerCase() === call.action.resolve.author.trim().toLowerCase();
            });

          case 'machine':
          case 'unit':
            return values.some((value) =>
              (call.tags || []).some((t) => t?.value.toLowerCase().includes(value.toLowerCase())),
            );

          case 'requestedRole':
            return values.some((value) => value.trim().toLowerCase() === call.requestedSupport?.trim().toLowerCase());
          default:
            return true;
        }
      });
    };
  }, [filters, timeFilter]);

  return (
    <CompanyContext value={{ companyId }}>
      <ProgressBar reset={networkStatus === 6} />
      <ResponsiveGridContainer>
        <Grid item xs={12}>
          <Paper>
            <ResponsiveCardContent>
              <Grid container spacing={3}>
                <Grid item md={7} xs={12}>
                  <Box mt={1}>
                    <TimePicker />
                  </Box>
                </Grid>
                <Grid item md={5} xs={12}>
                  <AutosuggestCardHeader
                    suggestionBase={extendedSuggestions}
                    onSuggestions={onFilters}
                    placeholderText={t(['batches:callFilters'], { defaultValue: 'Filter Calls...' })}
                    supportsSearch={false}
                  />
                </Grid>
              </Grid>
              <AndonTable calls={calls} filter={combinedFilters} />
            </ResponsiveCardContent>
          </Paper>
        </Grid>
      </ResponsiveGridContainer>
    </CompanyContext>
  );
};

export default CallsOverview;
